import {
  addDays,
  differenceInBusinessDays,
  differenceInCalendarDays,
  // eslint-disable-next-line no-restricted-imports
  format,
  isSameDay,
  subDays,
} from 'date-fns';
import { useMemo, useState } from 'react';

import { range } from 'lib/arrays';

import { DateOrFixturePicker } from '../DateOrFixturePicker';
import { DatePickerItem } from '../DatePickerItem';

type Props = {
  value: Date;
  minDate?: Date;
  maxDate?: Date;
  onChange: (date: Date) => void;
  disablePast?: boolean;
};
const PAGE_SIZE = 10;

const toPickerValue = (date: Date) => ({
  slug: format(date, 'yyyy-MM-dd'),
  date,
});

const DEFAULT_MAX_DATE = addDays(new Date(), 7);
export const DatePicker = ({
  value,
  minDate,
  maxDate = DEFAULT_MAX_DATE,
  onChange,
  disablePast,
}: Props) => {
  const [dates, setDates] = useState(
    range(PAGE_SIZE + 7).map(i => {
      const date = addDays(
        new Date(value),
        // add up to 7 days in the future
        7 - i
      );
      return toPickerValue(date);
    })
  );

  // picker value should only changes when value changes
  // because DateOrFixturePicker relies on it to scroll to the selected date
  const pickerValue = useMemo(() => toPickerValue(value), [value]);

  const loadMore = useMemo(() => {
    if (
      minDate &&
      differenceInBusinessDays(dates[dates.length - 1]?.date, minDate) <= 0
    ) {
      return undefined;
    }
    return () => {
      setDates(prevDates => {
        const oldestDate = prevDates[prevDates.length - 1].date;
        return [
          ...prevDates,
          ...range(PAGE_SIZE)
            .map(i => {
              const date = subDays(oldestDate, i + 1);
              return (
                (!minDate || differenceInBusinessDays(date, minDate) >= 0) &&
                toPickerValue(date)
              );
            })
            .filter(Boolean),
        ];
      });
    };
  }, [minDate, dates]);

  const loadMoreInFuture = useMemo(() => {
    if (differenceInBusinessDays(dates[0]?.date, maxDate) >= 0) {
      return undefined;
    }
    return () => {
      setDates(prevDates => {
        const newestDate = prevDates[0].date;
        return [
          ...range(PAGE_SIZE)
            .map(i => {
              const date = addDays(newestDate, PAGE_SIZE - i);
              return (
                differenceInBusinessDays(date, maxDate) <= 0 &&
                toPickerValue(date)
              );
            })
            .filter(Boolean),
          ...prevDates,
        ];
      });
    };
  }, [maxDate, dates]);

  return (
    <DateOrFixturePicker
      loadMore={loadMore}
      loadMoreInFuture={loadMoreInFuture}
      renderPlaceholder={() => <DatePickerItem loading />}
      renderItem={({ item }) => (
        <DatePickerItem
          date={item.date}
          onClick={() => {
            onChange(item.date);
          }}
          selected={isSameDay(item.date, value)}
          disabled={
            disablePast && differenceInCalendarDays(item.date, new Date()) < 0
          }
        />
      )}
      value={pickerValue}
      items={dates}
    />
  );
};
